/**
 * MARK:- Custom Cart Delivery Selector (v2 - Modal Based)
 * Handles delay delivery feature with modal UI and button-style variant picker pattern.
 *
 * FLOW:
 * 1. Customer clicks "Delay My Delivery" button in cart
 * 2. Modal opens with month buttons (horizontal row)
 * 3. First available month + week pre-selected as "Recommended"
 * 4. Customer selects month → weeks update
 * 5. Customer selects week or "None of these work"
 * 6. Confirm button saves to cart attributes and closes modal
 *
 * CART ATTRIBUTES SET:
 * - delivery_date: ISO date string (e.g., "2026-03-08")
 * - delivery_note: Custom text if "none of these work" selected
 *
 * LISTENS TO:
 * - zone:updated - Recalculates when customer changes their zone
 * - on:cart:add - Re-validates delivery date when items added to cart
 * - on:line-item:change - Re-validates delivery date when quantities change
 *
 * REQUIRES:
 * - window.deliverySettings.storefrontApiToken
 * - window.deliverySettings.shopDomain
 * - window.deliverySettings.variantReadyDates (from Liquid)
 */

(function() {
  'use strict';

  // MARK:- Module state
  let currentZone = null;
  let zoneSpringShipDate = null;
  let zoneLastShipDate = null; // MARK:- Last ship date ceiling from zone metaobject
  let lastAllowedDate = null; // MARK:- Parsed ceiling date for filtering
  let firstAvailableDate = null;
  let recommendedWeekDate = null; // The globally recommended week (first available)
  let selectedMonth = null;
  let selectedWeekStartDate = null;
  let isNoneSelected = false;
  let savedDeliveryNote = ''; // MARK:- Persisted note text
  let isInitialized = false;
  let variantReadyDateMap = {}; // MARK:- Liquid-provided variant ID → ready date map
  let cartChangeDebounceTimer = null; // MARK:- Debounce timer for cart change events

  // MARK:- DOM Elements cache
  const elements = {};

  // MARK:- Initialize when DOM is ready
  document.addEventListener('DOMContentLoaded', init);

  /**
   * Main initialization function
   */
  function init() {
    // MARK:- Cache DOM elements
    elements.modal = document.getElementById('delivery-options-modal');
    if (!elements.modal) return; // Exit if modal not on page

    elements.loading = document.getElementById('DeliveryModalLoading');
    elements.error = document.getElementById('DeliveryModalError');
    elements.selectors = document.getElementById('DeliveryModalSelectors');
    elements.monthButtons = document.getElementById('DeliveryMonthButtons');
    elements.weeksFieldset = document.getElementById('DeliveryWeeksFieldset');
    elements.weekButtons = document.getElementById('DeliveryWeekButtons');
    elements.noneRadio = document.getElementById('DeliveryNoneRadio');
    elements.noteField = document.getElementById('DeliveryNoteField');
    elements.noteTextarea = document.getElementById('DeliveryNoteTextarea');
    elements.footer = document.getElementById('DeliveryModalFooter');
    elements.confirmBtn = document.getElementById('DeliveryConfirmBtn');
    elements.confirmationDisplay = document.getElementById('CartDeliveryConfirmation');
    elements.confirmationDate = document.getElementById('CartDeliveryConfirmationDate');

    // MARK:- Zone confirmation elements
    elements.zoneConfirmed = document.getElementById('DeliveryZoneConfirmed');
    elements.zoneRequired = document.getElementById('DeliveryZoneRequired');
    elements.zipValue = document.getElementById('DeliveryZipValue');
    elements.zoneValue = document.getElementById('DeliveryZoneValue');

    // MARK:- Get current zone from localStorage
    currentZone = localStorage.getItem('growing_zone');

    // MARK:- Set up event listeners
    setupEventListeners();

    // MARK:- Load existing selection from cart
    loadExistingSelection();

    // MARK:- Copy Liquid-provided variant ready date map into module state
    variantReadyDateMap = { ...(window.deliverySettings?.variantReadyDateMap || {}) };

    isInitialized = true;
  }

  /**
   * Set up all event listeners
   */
  function setupEventListeners() {
    // MARK:- Modal open observer (modal-dialog component uses 'open' attribute)
    if (elements.modal) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'open') {
            if (elements.modal.hasAttribute('open')) {
              handleModalOpen();
            }
          }
        });
      });
      observer.observe(elements.modal, { attributes: true });
    }

    // MARK:- "None of these work" radio
    if (elements.noneRadio) {
      elements.noneRadio.addEventListener('change', handleNoneSelected);
    }

    // MARK:- Note textarea
    if (elements.noteTextarea) {
      elements.noteTextarea.addEventListener('input', debounce(handleNoteChange, 500));
    }

    // MARK:- Confirm button
    if (elements.confirmBtn) {
      elements.confirmBtn.addEventListener('click', handleConfirm);
    }


    // MARK:- Listen for zone updates from grow-zone-finder
    document.addEventListener('zone:updated', handleZoneUpdate);

    // MARK:- Listen for cart changes to re-validate delivery date
    document.addEventListener('on:cart:add', handleCartChange);
    document.addEventListener('on:line-item:change', handleCartChange);
  }

  /**
   * Load existing selection from cart attributes.
   * MARK:- After restoring the saved date, validates it against the fresh
   * Liquid-provided variantReadyDates. If any product now requires a later
   * date than what was saved, the selection is cleared and the user is prompted.
   */
  async function loadExistingSelection() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const deliveryDate = cart.attributes?.delivery_date;
      const deliveryNote = cart.attributes?.delivery_note;

      if (deliveryDate) {
        // MARK:- Validate saved date against current variant ready dates
        const variantReadyDates = window.deliverySettings?.variantReadyDates || [];
        const savedDate = parseLocalDate(deliveryDate);
        let latestReadyDate = null;

        variantReadyDates.forEach(dateStr => {
          if (dateStr && typeof dateStr === 'string') {
            const readyDate = parseLocalDate(dateStr);
            if (!latestReadyDate || readyDate > latestReadyDate) {
              latestReadyDate = readyDate;
            }
          }
        });

        if (latestReadyDate && savedDate < latestReadyDate) {
          // MARK:- Saved delivery date is before a product's ready date — invalid.
          // Clear stale cart attributes and immediately update the DOM so the
          // Liquid-rendered confirmation is replaced with a warning.
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              attributes: {
                delivery_date: '',
                delivery_note: ''
              }
            })
          });
          // MARK:- Reset the stale Liquid-rendered confirmation and show warning
          resetDeliveryDisplay('A product in your cart requires a later delivery date. Please select a new delivery window.');
          return;
        }

        selectedWeekStartDate = deliveryDate;
        const date = new Date(deliveryDate);
        selectedMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      }

      if (deliveryNote && !deliveryDate) {
        isNoneSelected = true;
        savedDeliveryNote = deliveryNote;
      }
    } catch (error) {
      console.error('MARK:- Error loading existing selection:', error);
    }
  }

  /**
   * Handle modal open event
   */
  function handleModalOpen() {
    // MARK:- Get current zone and ZIP from localStorage
    currentZone = localStorage.getItem('growing_zone');
    const savedZip = localStorage.getItem('growing_zipcode');

    // MARK:- Show appropriate section based on zone status
    if (currentZone && savedZip) {
      // MARK:- Zone is set - show confirmation and load delivery options
      showZoneConfirmed(currentZone, savedZip);
      showLoading();

      // MARK:- Fetch zone ship date and initialize
      if (!zoneSpringShipDate) {
        fetchZoneShipDate(currentZone);
      } else {
        initializeSelectors();
      }
    } else {
      // MARK:- No zone yet - show prompt to enter ZIP
      showZoneRequired();
      hideSelectors();
      hideFooter();
    }
  }

  /**
   * Handle zone update event from grow-zone-finder
   */
  function handleZoneUpdate(event) {
    const { zoneNumber, zipCode } = event.detail;

    if (zoneNumber) {
      // MARK:- Reset zone-dependent data when zone changes
      if (zoneNumber !== currentZone) {
        currentZone = zoneNumber;
        zoneSpringShipDate = null;
        zoneLastShipDate = null;
        lastAllowedDate = null;
        firstAvailableDate = null;
        recommendedWeekDate = null;
      }

      // MARK:- If modal is open, refresh with new zone
      if (elements.modal?.hasAttribute('open')) {
        showZoneConfirmed(zoneNumber, zipCode || localStorage.getItem('growing_zipcode'));
        showLoading();
        fetchZoneShipDate(zoneNumber);
      }
    }
  }

  // ============================================
  // MARK:- API Functions
  // ============================================

  /**
   * MARK:- Get Storefront API cart ID from AJAX API cart token
   * @returns {Promise<string|null>} Full cart GID or null
   */
  async function getStorefrontCartId() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      if (!cart.token) {
        console.error('MARK:- No cart token available');
        return null;
      }
      return `gid://shopify/Cart/${cart.token}`;
    } catch (error) {
      console.error('MARK:- Error getting cart token:', error);
      return null;
    }
  }

  /**
   * MARK:- Set cart metafields via Storefront API
   * @param {Array<{namespace: string, key: string, type: string, value: string}>} metafields
   * @returns {Promise<boolean>} Success status
   */
  async function setCartMetafields(metafields) {
    const settings = window.deliverySettings || {};
    const storefrontApiToken = settings.storefrontApiToken;
    const shopDomain = settings.shopDomain;

    if (!storefrontApiToken || !shopDomain) {
      console.error('MARK:- Storefront API settings not configured');
      return false;
    }

    const cartId = await getStorefrontCartId();
    if (!cartId) return false;

    // MARK:- Storefront API uses "namespace.key" format, not separate namespace field
    const metafieldInputs = metafields.map(mf => ({
      ownerId: cartId,
      key: `${mf.namespace}.${mf.key}`,
      type: mf.type,
      value: mf.value
    }));

    const graphqlQuery = {
      query: `
        mutation cartMetafieldsSet($metafields: [CartMetafieldsSetInput!]!) {
          cartMetafieldsSet(metafields: $metafields) {
            metafields { key value }
            userErrors { field message code }
          }
        }
      `,
      variables: { metafields: metafieldInputs }
    };

    try {
      const response = await fetch(`https://${shopDomain}/api/2025-10/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storefrontApiToken
        },
        body: JSON.stringify(graphqlQuery)
      });

      const data = await response.json();
      if (data.errors || data.data?.cartMetafieldsSet?.userErrors?.length > 0) {
        console.error('MARK:- Cart metafield errors:', data.errors || data.data.cartMetafieldsSet.userErrors);
        return false;
      }
      return true;
    } catch (error) {
      console.error('MARK:- Error setting cart metafields:', error);
      return false;
    }
  }

  /**
   * Fetch zone spring ship date from growing_zone_rule metaobject
   * @param {string} zoneNumber - Growing zone number (e.g., "5", "7a")
   */
  async function fetchZoneShipDate(zoneNumber) {
    const settings = window.deliverySettings || {};
    const storefrontApiToken = settings.storefrontApiToken;
    const shopDomain = settings.shopDomain;

    if (!storefrontApiToken || !shopDomain) {
      console.error('MARK:- Delivery settings not configured');
      hideLoading();
      showError('Configuration error. Please try again later.');
      return;
    }

    // MARK:- Construct handle (zone-5, zone-7a, etc.)
    const handle = `zone-${zoneNumber.toLowerCase()}`;

    const graphqlQuery = {
      query: `{
        metaobject(handle: { handle: "${handle}", type: "growing_zone_rule" }) {
          zoneName: field(key: "zone_name") { value }
          springShipDate: field(key: "spring_ship_date") { value }
          lastShipDate: field(key: "last_ship_date") { value }
        }
      }`
    };

    try {
      const response = await fetch(`https://${shopDomain}/api/2025-10/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storefrontApiToken
        },
        body: JSON.stringify(graphqlQuery)
      });

      const data = await response.json();
      const springShipDate = data?.data?.metaobject?.springShipDate?.value;
      const lastShipDate = data?.data?.metaobject?.lastShipDate?.value;

      if (springShipDate) {
        // MARK:- Parse date as local time to avoid timezone issues
        zoneSpringShipDate = parseLocalDate(springShipDate);
      } else {
        console.warn('MARK:- No spring ship date found for zone:', zoneNumber);
        // MARK:- Fall back to today + 7 days
        zoneSpringShipDate = new Date();
        zoneSpringShipDate.setDate(zoneSpringShipDate.getDate() + 7);
      }

      // MARK:- Capture last ship date ceiling if configured
      if (lastShipDate) {
        zoneLastShipDate = parseLocalDate(lastShipDate);
        lastAllowedDate = new Date(zoneLastShipDate);
      } else {
        zoneLastShipDate = null;
        lastAllowedDate = null;
      }

      initializeSelectors();

    } catch (error) {
      console.error('MARK:- Error fetching zone ship date:', error);
      hideLoading();
      showError('Unable to load delivery options. Please try again.');
    }
  }

  // ============================================
  // MARK:- Selector Initialization
  // ============================================

  /**
   * Initialize month and week selectors
   */
  function initializeSelectors() {
    calculateFirstAvailableDate();

    // MARK:- Edge case: last allowed date is before first available date (no valid window)
    if (lastAllowedDate && firstAvailableDate && lastAllowedDate < firstAvailableDate) {
      hideLoading();
      showError('No delivery windows are currently available for your zone. Please contact us for assistance.');
      hideSelectors();
      hideFooter();
      return;
    }

    generateMonthButtons();
    hideLoading();
    showSelectors();
    showFooter();

    // MARK:- Restore "none" selection state if previously selected
    if (isNoneSelected) {
      if (elements.noneRadio) elements.noneRadio.checked = true;
      if (elements.noteTextarea) elements.noteTextarea.value = savedDeliveryNote;
      showNoteField();
    }
  }

  /**
   * Calculate first available date from zone ship date and variant ready dates
   */
  function calculateFirstAvailableDate() {
    // MARK:- Start with zone spring ship date
    let earliest = zoneSpringShipDate ? new Date(zoneSpringShipDate) : new Date();

    // MARK:- Get variant ready dates from window.deliverySettings
    const variantReadyDates = window.deliverySettings?.variantReadyDates || [];

    // MARK:- Find the latest ready date among all cart items
    variantReadyDates.forEach(dateStr => {
      if (dateStr && typeof dateStr === 'string') {
        // MARK:- Parse as local time to avoid timezone issues
        const readyDate = parseLocalDate(dateStr);
        if (readyDate > earliest) {
          earliest = readyDate;
        }
      }
    });

    // MARK:- Ensure first available is not in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (earliest < today) {
      earliest = today;
    }

    firstAvailableDate = earliest;

    // MARK:- Set the recommended week date to the Sunday of the week containing firstAvailableDate
    const recommendedWeekStart = getWeekStart(firstAvailableDate);
    recommendedWeekDate = formatISODate(recommendedWeekStart);

    return firstAvailableDate;
  }

  /**
   * Get the Sunday that starts the week containing the given date
   */
  function getWeekStart(date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0); // Normalize to midnight
    const day = d.getDay(); // 0 = Sunday
    d.setDate(d.getDate() - day);
    return d;
  }

  /**
   * Generate month buttons (horizontal row)
   */
  function generateMonthButtons() {
    if (!elements.monthButtons || !firstAvailableDate) return;

    elements.monthButtons.innerHTML = '';

    const startDate = new Date(firstAvailableDate);

    // MARK:- Generate up to 3 months of options, skipping months past lastAllowedDate
    let firstRendered = true;
    for (let i = 0; i < 3; i++) {
      const monthDate = new Date(startDate.getFullYear(), startDate.getMonth() + i, 1);

      // MARK:- Skip months where the 1st is after lastAllowedDate
      if (lastAllowedDate && monthDate > lastAllowedDate) break;

      const monthValue = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = monthDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      const isFirst = firstRendered;
      const isSelected = selectedMonth ? (selectedMonth === monthValue) : isFirst;

      const button = createMonthButton(monthValue, monthLabel, isFirst, isSelected);
      elements.monthButtons.appendChild(button);

      // MARK:- Pre-select first month if none selected
      if (isSelected) {
        selectedMonth = monthValue;
      }

      firstRendered = false;
    }

    // MARK:- Generate weeks for selected month
    generateWeekButtons(selectedMonth);
  }

  /**
   * Create a month button element
   */
  function createMonthButton(value, label, isRecommended, isChecked) {
    const id = `month-${value}`;

    // MARK:- Container with recommended class if applicable
    const container = document.createElement('div');
    container.className = `delivery-modal__month-option${isRecommended ? ' delivery-modal__month-option--recommended' : ''}`;

    // MARK:- Hidden radio input
    const input = document.createElement('input');
    input.type = 'radio';
    input.className = 'opt-btn visually-hidden';
    input.name = 'delivery-month';
    input.id = id;
    input.value = value;
    if (isChecked) {
      input.checked = true;
      input.setAttribute('checked', 'checked');
    }

    // MARK:- Visible label (button style)
    const labelEl = document.createElement('label');
    labelEl.className = `opt-label opt-label--btn btn relative text-center${isRecommended ? ' delivery-modal__month-label--recommended' : ''}`;
    labelEl.setAttribute('for', id);

    // MARK:- Listen for input change (native label click triggers this)
    input.addEventListener('change', function() {
      if (this.checked) {
        handleMonthSelect(value);
      }
    });

    const labelText = document.createElement('span');
    labelText.className = 'block';
    labelText.textContent = label;
    labelEl.appendChild(labelText);

    if (isRecommended) {
      const recommendedBadge = document.createElement('span');
      recommendedBadge.className = 'block text-xs';
      recommendedBadge.textContent = 'Recommended';
      labelEl.appendChild(recommendedBadge);
    }

    container.appendChild(input);
    container.appendChild(labelEl);

    return container;
  }

  /**
   * Handle month button selection
   */
  function handleMonthSelect(monthValue) {
    selectedMonth = monthValue;
    // MARK:- Reset week selection
    selectedWeekStartDate = null;
    isNoneSelected = false;
    if (elements.noneRadio) elements.noneRadio.checked = false;
    hideNoteField();

    generateWeekButtons(monthValue);
    showWeeks();
  }

  /**
   * Generate week buttons (vertical stack)
   */
  function generateWeekButtons(monthValue) {
    if (!elements.weekButtons || !monthValue) return;

    elements.weekButtons.innerHTML = '';

    const [year, month] = monthValue.split('-').map(Number);
    const weeks = getWeeksInMonth(year, month - 1);

    weeks.forEach((week) => {
      // MARK:- Skip weeks that are entirely before first available date
      if (week.end < firstAvailableDate) return;

      // MARK:- Skip weeks that start after the last allowed date
      if (lastAllowedDate && week.start > lastAllowedDate) return;

      // MARK:- Use the actual week start date (Sunday)
      const weekStartDate = week.start;
      const weekIsoDate = formatISODate(weekStartDate);

      // MARK:- Only the globally first available week is recommended
      const isRecommended = (weekIsoDate === recommendedWeekDate);

      // MARK:- Check if this week is selected (don't pre-select if "none" was chosen)
      let isSelected = false;
      if (selectedWeekStartDate) {
        isSelected = (weekIsoDate === selectedWeekStartDate);
      } else if (!isNoneSelected && isRecommended) {
        isSelected = true;
      }

      const button = createWeekButton(weekStartDate, isRecommended, isSelected);
      elements.weekButtons.appendChild(button);

      // MARK:- Pre-select recommended week if none selected and not in "none" mode
      if (isSelected && !selectedWeekStartDate && !isNoneSelected) {
        selectedWeekStartDate = weekIsoDate;
      }
    });

    // MARK:- If no weeks available, show message
    if (elements.weekButtons.children.length === 0) {
      const msg = document.createElement('p');
      msg.className = 'text-sm opacity-70';
      msg.textContent = 'No available weeks in this month.';
      elements.weekButtons.appendChild(msg);
    }

    showWeeks();
  }

  /**
   * Create a week button element (vertical full-width)
   */
  function createWeekButton(startDate, isRecommended, isChecked) {
    const isoDate = formatISODate(startDate);
    const id = `week-${isoDate}`;
    const displayDate = startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

    // MARK:- Container with recommended class
    const container = document.createElement('div');
    container.className = `delivery-modal__week-option${isRecommended ? ' delivery-modal__week-option--recommended' : ''}`;

    // MARK:- Hidden radio input
    const input = document.createElement('input');
    input.type = 'radio';
    input.className = 'opt-btn visually-hidden';
    input.name = 'delivery-week';
    input.id = id;
    input.value = isoDate;
    if (isChecked) {
      input.checked = true;
      input.setAttribute('checked', 'checked');
    }

    // MARK:- Listen for input change (native label click triggers this)
    input.addEventListener('change', function() {
      if (this.checked) {
        handleWeekSelect(isoDate);
      }
    });

    // MARK:- Visible label (full-width button style)
    const labelEl = document.createElement('label');
    labelEl.className = 'delivery-modal__week-label';
    labelEl.setAttribute('for', id);

    // MARK:- Text container with date and subtext
    const textContainer = document.createElement('span');
    textContainer.className = 'delivery-modal__week-text';

    const dateSpan = document.createElement('span');
    dateSpan.className = 'delivery-modal__week-date';
    dateSpan.textContent = `Week of ${displayDate}`;
    textContainer.appendChild(dateSpan);

    if (isRecommended) {
      const subtextSpan = document.createElement('span');
      subtextSpan.className = 'delivery-modal__week-subtext';
      subtextSpan.textContent = 'Optimal for your growing zone';
      textContainer.appendChild(subtextSpan);
    }

    labelEl.appendChild(textContainer);

    // MARK:- Recommended badge
    if (isRecommended) {
      const badge = document.createElement('span');
      badge.className = 'delivery-modal__week-badge';
      badge.textContent = 'Recommended';
      labelEl.appendChild(badge);
    }

    container.appendChild(input);
    container.appendChild(labelEl);

    return container;
  }

  /**
   * Handle week button selection
   */
  function handleWeekSelect(isoDate) {
    selectedWeekStartDate = isoDate;
    isNoneSelected = false;

    // MARK:- Uncheck "none" option
    if (elements.noneRadio) elements.noneRadio.checked = false;
    hideNoteField();
  }

  /**
   * Handle "none of these work" selection
   */
  function handleNoneSelected(event) {
    if (event.target.checked) {
      isNoneSelected = true;
      selectedWeekStartDate = null;

      // MARK:- Uncheck any week radio buttons
      const weekRadios = elements.weekButtons?.querySelectorAll('input[type="radio"]');
      weekRadios?.forEach(radio => radio.checked = false);

      showNoteField();
    }
  }

  /**
   * Handle note textarea change
   */
  function handleNoteChange() {
    // Note will be saved on confirm
  }

  /**
   * Handle confirm button click
   */
  async function handleConfirm() {
    // MARK:- Validate selected date is not past last allowed date
    if (selectedWeekStartDate && lastAllowedDate) {
      const selectedDate = parseLocalDate(selectedWeekStartDate);
      if (selectedDate > lastAllowedDate) {
        showError('The selected delivery date is no longer available. Please choose another date.');
        return;
      }
    }

    try {
      const attributes = {};
      const metafields = [];

      if (selectedWeekStartDate && !isNoneSelected) {
        attributes.delivery_date = selectedWeekStartDate;
        attributes.delivery_note = '';
        savedDeliveryNote = '';

        metafields.push({
          namespace: 'custom',
          key: 'delivery_date',
          type: 'date',
          value: selectedWeekStartDate
        });
      } else if (isNoneSelected) {
        attributes.delivery_date = '';
        savedDeliveryNote = elements.noteTextarea?.value?.trim() || 'Customer requested alternative date';
        attributes.delivery_note = savedDeliveryNote;

        metafields.push({
          namespace: 'custom',
          key: 'delivery_note',
          type: 'single_line_text_field',
          value: savedDeliveryNote
        });
      }

      // MARK:- Write to Storefront API cart metafields (for order copying)
      const metafieldSuccess = await setCartMetafields(metafields);
      if (!metafieldSuccess) {
        console.warn('MARK:- Failed to set cart metafields, continuing with attributes only');
      }

      // MARK:- Write to AJAX API cart attributes (for Liquid template reads)
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ attributes })
      });

      // MARK:- Update confirmation display in cart
      updateConfirmationDisplay();

      // MARK:- Close modal
      closeModal();

    } catch (error) {
      console.error('MARK:- Error saving delivery selection:', error);
      showError('Unable to save. Please try again.');
    }
  }

  /**
   * Update the confirmation display in the cart
   * MARK:- Uses unified display for both date and note selections
   */
  function updateConfirmationDisplay() {
    const confirmationEl = document.getElementById('CartDeliveryConfirmation');
    const dateEl = document.getElementById('CartDeliveryConfirmationDate');
    const noteEl = document.getElementById('CartDeliveryNoteDisplay');
    const triggerEl = document.getElementById('CartDeliveryTrigger');
    const triggerBtn = triggerEl?.querySelector('modal-opener');

    // MARK:- Hide trigger button for both cases
    if (triggerBtn) triggerBtn.style.display = 'none';

    // MARK:- Determine display content
    let displayText = '';
    let displayLabel = 'Delivery:';

    if (selectedWeekStartDate && !isNoneSelected) {
      // MARK:- Parse date string directly to avoid timezone issues
      const [year, month, day] = selectedWeekStartDate.split('-');
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                          'July', 'August', 'September', 'October', 'November', 'December'];
      displayText = `Week of ${monthNames[parseInt(month) - 1]} ${parseInt(day)}, ${year}`;
    } else if (isNoneSelected) {
      displayLabel = 'Delivery Note:';
      displayText = savedDeliveryNote || 'Alternative date requested';
    }

    // MARK:- Remove old note element if exists (we're unifying to single display)
    if (noteEl) noteEl.remove();

    // MARK:- Update or create confirmation element
    if (confirmationEl) {
      if (dateEl) dateEl.textContent = displayText;
      // MARK:- Update label text
      const labelSpan = confirmationEl.querySelector('span:not(strong)');
      if (labelSpan) {
        labelSpan.innerHTML = `${displayLabel} <strong id="CartDeliveryConfirmationDate">${displayText}</strong>`;
      }
      confirmationEl.style.display = '';
    } else if (triggerEl) {
      const newConfirmation = document.createElement('div');
      newConfirmation.className = 'cart-delivery-trigger__confirmation mt-3';
      newConfirmation.id = 'CartDeliveryConfirmation';
      newConfirmation.innerHTML = `
        <div class="flex items-center gap-2 justify-center text-sm">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
          </svg>
          <span>${displayLabel} <strong id="CartDeliveryConfirmationDate">${displayText}</strong></span>
        </div>
        <button type="button" class="cart-delivery-trigger__change text-xs underline mt-1 block mx-auto opacity-70 hover:opacity-100" onclick="document.getElementById('delivery-options-modal')?.setAttribute('open', '')">
          Change
        </button>
      `;
      triggerEl.appendChild(newConfirmation);
    }
  }

  /**
   * Close the modal
   */
  function closeModal() {
    if (elements.modal && typeof elements.modal.close === 'function') {
      elements.modal.close();
    } else if (elements.modal) {
      elements.modal.removeAttribute('open');
    }
  }

  // ============================================
  // MARK:- Cart Change Re-validation
  // ============================================

  /**
   * Handle cart change events (on:cart:add, on:line-item:change)
   * Debounces rapid changes and re-validates the current delivery selection
   */
  function handleCartChange(event) {
    // MARK:- Skip if no delivery date is currently selected
    if (!selectedWeekStartDate && !isNoneSelected) return;

    // MARK:- Debounce at 600ms for rapid quantity changes
    clearTimeout(cartChangeDebounceTimer);
    cartChangeDebounceTimer = setTimeout(() => {
      revalidateDeliveryDate(event.detail?.cart);
    }, 600);
  }

  /**
   * Re-validate the current delivery selection against fresh cart data.
   * MARK:- Uses variantReadyDateMap as the primary lookup. When AJAX-added
   * variants are missing from the map, fetches their ready_date metafields
   * from the Storefront API so conflicts are never silently missed.
   * @param {Object} [cartData] - Cart data from the event, or fetched if not provided
   */
  async function revalidateDeliveryDate(cartData) {
    try {
      if (!cartData) {
        const response = await fetch('/cart.js');
        cartData = await response.json();
      }

      // MARK:- If cart is empty, clear selection silently
      if (!cartData.items || cartData.items.length === 0) {
        clearDeliverySelection();
        return;
      }

      // MARK:- Identify variants missing from the Liquid-provided map
      const missingVariantIds = [];
      cartData.items.forEach(item => {
        if (!(item.variant_id in variantReadyDateMap)) {
          missingVariantIds.push(item.variant_id);
        }
      });

      // MARK:- Fetch ready dates for unknown variants via Storefront API
      if (missingVariantIds.length > 0) {
        await fetchMissingVariantReadyDates(missingVariantIds);
      }

      // MARK:- Build effective ready dates from the variant map
      // Only include dates for variants currently in the cart
      const effectiveDates = [];
      cartData.items.forEach(item => {
        const date = variantReadyDateMap[item.variant_id];
        if (date && typeof date === 'string') effectiveDates.push(date);
      });

      // MARK:- Update with map-derived data
      window.deliverySettings.variantReadyDates = effectiveDates;

      calculateFirstAvailableDate();
      validateCurrentSelection();

    } catch (error) {
      console.error('MARK:- Error re-validating delivery date:', error);
    }
  }

  /**
   * Fetch ready_date metafields for variant IDs not in variantReadyDateMap.
   * MARK:- Uses the same Storefront API token/domain as fetchZoneShipDate().
   * Updates variantReadyDateMap in place so subsequent calls don't re-fetch.
   * @param {number[]} variantIds - Numeric variant IDs to look up
   */
  async function fetchMissingVariantReadyDates(variantIds) {
    const settings = window.deliverySettings || {};
    const storefrontApiToken = settings.storefrontApiToken;
    const shopDomain = settings.shopDomain;

    if (!storefrontApiToken || !shopDomain) return;

    // MARK:- Build GIDs for the Storefront API nodes query
    const gids = variantIds.map(id => `"gid://shopify/ProductVariant/${id}"`);

    const graphqlQuery = {
      query: `{
        nodes(ids: [${gids.join(', ')}]) {
          ... on ProductVariant {
            id
            metafield(namespace: "custom", key: "ready_date") { value }
          }
        }
      }`
    };

    try {
      const response = await fetch(`https://${shopDomain}/api/2025-10/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storefrontApiToken
        },
        body: JSON.stringify(graphqlQuery)
      });

      const data = await response.json();
      const nodes = data?.data?.nodes || [];

      nodes.forEach(node => {
        if (!node || !node.id) return;
        // MARK:- Extract numeric ID from GID string
        const numericId = parseInt(node.id.replace('gid://shopify/ProductVariant/', ''), 10);
        const readyDate = node.metafield?.value || null;
        // MARK:- Store in map (null means "checked, no ready_date") to avoid re-fetching
        variantReadyDateMap[numericId] = readyDate;
      });
    } catch (error) {
      console.error('MARK:- Error fetching variant ready dates:', error);
      // MARK:- Mark as checked (null) so we don't retry on every cart change
      variantIds.forEach(id => {
        if (!(id in variantReadyDateMap)) {
          variantReadyDateMap[id] = null;
        }
      });
    }
  }

  /**
   * Validate the current delivery selection against updated constraints
   */
  function validateCurrentSelection() {
    // MARK:- Only validate if a specific date is selected (not "none")
    if (!selectedWeekStartDate || isNoneSelected) return;

    const selectedDate = parseLocalDate(selectedWeekStartDate);
    let reason = '';

    // MARK:- Check if selected date is now before the first available date
    if (firstAvailableDate && selectedDate < firstAvailableDate) {
      reason = 'Your selected delivery date is no longer available due to cart changes. Please select a new date.';
    }

    // MARK:- Check if selected date is after the last allowed date
    if (!reason && lastAllowedDate && selectedDate > lastAllowedDate) {
      reason = 'Your selected delivery date is past the shipping window for your zone. Please select a new date.';
    }

    if (reason) {
      clearDeliverySelection(reason);
    }
  }

  /**
   * Clear the current delivery selection and update cart attributes
   * @param {string} [reason] - Optional reason message to display
   */
  async function clearDeliverySelection(reason) {
    // MARK:- Reset module state
    selectedWeekStartDate = null;
    selectedMonth = null;
    isNoneSelected = false;
    savedDeliveryNote = '';

    // MARK:- Clear cart metafields via Storefront API
    await setCartMetafields([
      { namespace: 'custom', key: 'delivery_date', type: 'date', value: '' },
      { namespace: 'custom', key: 'delivery_note', type: 'single_line_text_field', value: '' }
    ]).catch(err => console.warn('MARK:- Failed to clear metafields:', err));

    // MARK:- Clear cart attributes via AJAX API
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          attributes: {
            delivery_date: '',
            delivery_note: ''
          }
        })
      });
    } catch (error) {
      console.error('MARK:- Error clearing delivery attributes:', error);
    }

    // MARK:- Update DOM to reflect cleared state
    resetDeliveryDisplay(reason);
  }

  /**
   * Reset the delivery display in the cart - remove confirmation, show trigger button
   * @param {string} [message] - Optional warning message to display
   */
  function resetDeliveryDisplay(message) {
    // MARK:- Remove confirmation element
    const confirmationEl = document.getElementById('CartDeliveryConfirmation');
    if (confirmationEl) confirmationEl.remove();

    // MARK:- Show trigger button
    const triggerEl = document.getElementById('CartDeliveryTrigger');
    if (triggerEl) {
      const triggerBtn = triggerEl.querySelector('modal-opener');
      if (triggerBtn) {
        triggerBtn.style.display = '';
      } else {
        // MARK:- Recreate trigger button if it was removed
        const buttonText = window.deliverySettings?.buttonText || 'Schedule My Delivery';
        const newOpener = document.createElement('modal-opener');
        newOpener.setAttribute('data-modal', '#delivery-options-modal');
        newOpener.innerHTML = `
          <button type="button" class="btn btn--secondary w-full">
            ${buttonText}
          </button>
        `;
        triggerEl.prepend(newOpener);
      }
    }

    // MARK:- Show persistent warning notice if reason provided
    // Warning stays visible until the user opens the modal to re-select
    if (message && triggerEl) {
      const existingWarning = triggerEl.querySelector('.delivery-warning-notice');
      if (existingWarning) existingWarning.remove();

      const warning = document.createElement('div');
      warning.className = 'delivery-warning-notice';
      warning.innerHTML = `
        <div class="flex items-start gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="flex-shrink-0 mt-0.5" aria-hidden="true">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>${message}</span>
        </div>
      `;
      triggerEl.appendChild(warning);
    }
  }

  // ============================================
  // MARK:- Date Calculation Helpers
  // ============================================

  /**
   * Get all weeks in a given month (only weeks whose Sunday falls in this month)
   */
  function getWeeksInMonth(year, month) {
    const weeks = [];
    const firstDay = new Date(year, month, 1);
    firstDay.setHours(0, 0, 0, 0);

    // MARK:- Find the first Sunday on or after the first of the month
    let currentSunday = new Date(firstDay);
    while (currentSunday.getDay() !== 0) {
      currentSunday.setDate(currentSunday.getDate() + 1);
    }

    // MARK:- Generate weeks starting from each Sunday that falls in this month
    while (currentSunday.getMonth() === month) {
      const weekStart = new Date(currentSunday);
      weekStart.setHours(0, 0, 0, 0);

      const weekEnd = new Date(currentSunday);
      weekEnd.setDate(weekEnd.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);

      weeks.push({
        start: weekStart,
        end: weekEnd
      });

      currentSunday.setDate(currentSunday.getDate() + 7);
    }

    return weeks;
  }

  /**
   * Parse a date string (YYYY-MM-DD) as local time to avoid timezone issues
   */
  function parseLocalDate(dateStr) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  /**
   * Format date to ISO string (YYYY-MM-DD)
   */
  function formatISODate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // ============================================
  // MARK:- UI Helper Functions
  // ============================================

  function showLoading() {
    if (elements.loading) elements.loading.hidden = false;
    if (elements.selectors) elements.selectors.hidden = true;
    if (elements.footer) elements.footer.hidden = true;
  }

  function hideLoading() {
    if (elements.loading) elements.loading.hidden = true;
  }

  function showError(message) {
    if (elements.error) {
      elements.error.textContent = message;
      elements.error.hidden = false;
    }
  }

  function hideError() {
    if (elements.error) elements.error.hidden = true;
  }

  function showSelectors() {
    if (elements.selectors) elements.selectors.hidden = false;
  }

  function showWeeks() {
    if (elements.weeksFieldset) elements.weeksFieldset.hidden = false;
  }

  function hideWeeks() {
    if (elements.weeksFieldset) elements.weeksFieldset.hidden = true;
  }

  function showNoteField() {
    if (elements.noteField) elements.noteField.hidden = false;
  }

  function hideNoteField() {
    if (elements.noteField) elements.noteField.hidden = true;
  }

  function showFooter() {
    if (elements.footer) elements.footer.hidden = false;
  }

  function hideFooter() {
    if (elements.footer) elements.footer.hidden = true;
  }

  function hideSelectors() {
    if (elements.selectors) elements.selectors.hidden = true;
  }

  function showZoneConfirmed(zone, zip) {
    // MARK:- Show confirmed zone section, hide required section
    if (elements.zoneConfirmed) elements.zoneConfirmed.hidden = false;
    if (elements.zoneRequired) elements.zoneRequired.hidden = true;
    if (elements.zoneValue) elements.zoneValue.textContent = zone;
    if (elements.zipValue) elements.zipValue.textContent = zip;
  }

  function showZoneRequired() {
    // MARK:- Show required section, hide confirmed section
    if (elements.zoneConfirmed) elements.zoneConfirmed.hidden = true;
    if (elements.zoneRequired) elements.zoneRequired.hidden = false;
  }

  // ============================================
  // MARK:- Utility Functions
  // ============================================

  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

})();
